import { useContext } from 'react';

import { AbilityBuilder, MongoAbility, createMongoAbility } from '@casl/ability';
import { createContextualCan } from '@casl/react';

import { RoleEnum } from '@/app/enums/RoleEnum';
import { invariant } from '@/app/utils/invariant';

import { AbilityContext } from './role-management-context';
import { Actions, Subjects } from './types';

function defineAbility(role?: RoleEnum) {
  const { can, cannot, build } = new AbilityBuilder<MongoAbility<[Actions, Subjects]>>(
    createMongoAbility,
  );

  switch (role) {
    case RoleEnum.ADMIN_MASTER:
      can('manage', 'all');
      break;

    case RoleEnum.ADMIN:
      can('read', 'Project');
      can('manage', 'Member');
      can('manage', 'Podcast');
      can('manage', 'Episode');
      can('manage', 'Music');

      cannot('update', 'Member');
      break;

    case RoleEnum.EDITOR:
      can('read', 'Project');
      can('manage', 'Podcast');
      can('manage', 'Episode');
      can('manage', 'Music');

      cannot('delete', 'Podcast');
      break;

    case RoleEnum.READER:
      can('read', 'Project');
      can('read', 'Podcast');
      can('read', 'Episode');
      can('read', 'Music');
      break;

    default:
      cannot('manage', 'all');
  }

  return build();
}

const useAbility = () => {
  const ability = useContext(AbilityContext);
  invariant(!!ability, 'useAbility must be used within AbilityProvider');
  return ability;
};

const Can = createContextualCan<MongoAbility<[Actions, Subjects]>>(AbilityContext.Consumer);

export { useAbility, Can, defineAbility };
